import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import AppWindowHelp from "../components/AppWindowHelp";
import OverlaySectionNavigator from "../components/common/OverlaySectionNavigator";
import useVestaFocusHighlight from "../hooks/useVestaFocusHighlight";
import { canAccessPage, getFirstAllowedRoute, getRoutePageKey, readStoredUser } from "../utils/permissions";
import { clearOverlayFieldValidation, validateActiveOverlay, validateOverlayField } from "../components/common/OverlayValidation";

function readCompanyIdentity() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
    return {
      name: tenant?.companyDisplayName || tenant?.displayName || user?.companyName || "VestaMNG",
      code: tenant?.dbCode || user?.companyCode || "",
      logoUrl: tenant?.logoUrl || "",
    };
  } catch {
    return { name: "VestaMNG", code: "", logoUrl: "" };
  }
}

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [pinned, setPinned] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  useVestaFocusHighlight(location);

  const [company, setCompany] = useState(readCompanyIdentity);

  useEffect(() => {
    const refreshCompany = () => setCompany(readCompanyIdentity());
    window.addEventListener("vesta:tenant-updated", refreshCompany);
    window.addEventListener("storage", refreshCompany);
    return () => {
      window.removeEventListener("vesta:tenant-updated", refreshCompany);
      window.removeEventListener("storage", refreshCompany);
    };
  }, []);

  const onNavigate = (to) => {
    const user = readStoredUser();
    const pageKey = getRoutePageKey(to);
    navigate(canAccessPage(user, pageKey, 1) ? to : getFirstAllowedRoute(user));
    if (!pinned) setCollapsed(true);
  };
  const onAnyUiAction = () => { if (!pinned) setCollapsed(true); };

  useEffect(() => {
    function validateOverlaySave(event) {
      const saveButton = event.target?.closest?.(".overlay_save_button");
      if (!saveButton || saveButton.disabled || !saveButton.closest(".overlay_card")) return;
      const overlayValid = validateActiveOverlay();
      const stayCard = saveButton.closest(".booking_stay_overlay_card");
      const activeStayTab = stayCard?.querySelector(".booking_stay_tab.is_active")?.dataset?.stayTab || "";
      if (activeStayTab) {
        document.dispatchEvent(new CustomEvent("vesta:stay-tab-validation", { detail: { tab: activeStayTab, invalid: !overlayValid } }));
      }
      if (overlayValid) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof saveButton.focus === "function") saveButton.focus({ preventScroll: true });
    }

    function clearFocusedValidation(event) {
      const field = event.target?.closest?.(".overlay_field");
      if (!field || !field.classList.contains("overlay_validation_invalid")) return;
      clearOverlayFieldValidation(field);
    }

    function clearResolvedValidation(event) {
      const field = event.target?.closest?.(".overlay_field");
      if (!field || !field.classList.contains("overlay_validation_invalid")) return;
      if (!validateOverlayField(field)) clearOverlayFieldValidation(field);
    }

    document.addEventListener("click", validateOverlaySave, true);
    document.addEventListener("input", clearResolvedValidation, true);
    document.addEventListener("change", clearResolvedValidation, true);
    document.addEventListener("focusin", clearFocusedValidation, true);
    return () => {
      document.removeEventListener("click", validateOverlaySave, true);
      document.removeEventListener("input", clearResolvedValidation, true);
      document.removeEventListener("change", clearResolvedValidation, true);
      document.removeEventListener("focusin", clearFocusedValidation, true);
    };
  }, []);

  useEffect(() => {
    const selectDropdownSelector = [
      ".vesta_select_trigger",
      ".vesta_select_menu",
      ".vesta_select_scroll",
      ".overlay_select_button",
      ".overlay_select_dropdown",
      ".overlay_select_dropdown_portal",
      ".booking_customer_autocomplete_field .overlay_input_action_wrap",
      ".booking_customer_autocomplete_dropdown",
      ".management_catalog_root_select_menu",
      ".management_catalog_root_select_scroll",
      ".booking_status_menu",
    ].join(", ");

    const isSelectDropdownScroll = (target) => Boolean(target?.closest?.(selectDropdownSelector));

    const closeDropdowns = (event) => {
      if (isSelectDropdownScroll(event?.target)) return;
      document.dispatchEvent(new CustomEvent("vesta:close-dropdowns"));
      const active = document.activeElement;
      if (active?.tagName === "SELECT" && typeof active.blur === "function") active.blur();
    };

    window.addEventListener("wheel", closeDropdowns, true);
    window.addEventListener("scroll", closeDropdowns, true);
    return () => {
      window.removeEventListener("wheel", closeDropdowns, true);
      window.removeEventListener("scroll", closeDropdowns, true);
    };
  }, []);

  return (
    <div className={`app_root ${collapsed ? "is_sidebar_collapsed" : "is_sidebar_open"}`}>
      <Sidebar collapsed={collapsed} pinned={pinned} onToggleCollapse={() => setCollapsed((v) => !v)} onTogglePin={() => setPinned((v) => !v)} activePath={location.pathname} onNavigate={onNavigate} />
      <Topbar companyName={company.name} companyCode={company.code} companyLogoUrl={company.logoUrl} onAnyAction={onAnyUiAction} />
      <main className="app_content" onMouseDown={onAnyUiAction}>
        <Outlet />
      </main>
      <AppWindowHelp />
      <OverlaySectionNavigator />
    </div>
  );
}
